import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { hasScanAnswer, isScanArchived, type ClimbScan } from '../../../types/climb-scan';

interface ScanListColumnProps {
  scans: ClimbScan[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  /**
   * Look at a wall. The first look at a photo nobody has asked about, the retry
   * of one that failed, and another look at one that came back wrong are all
   * this press. Absent for anyone who may not spend a model call on it — see
   * isSuperAdmin — which is what hides the button.
   */
  onScan?: (id: string) => void;
}

const STATUS_TONES: Record<ClimbScan['status'], string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  ready: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

/** Date and time together: two scans of the same wall on one day are ordinary. */
function formatTaken(iso: string): string {
  const date = new Date(iso);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * Every scan the gym has, newest first. Archived ones — those whose colours have
 * all become climbs — stay on the list greyed out rather than dropping off it:
 * a wall reset is worth being able to look back at once it is finished.
 */
export function ScanListColumn({
  scans,
  selectedId,
  onSelect,
  loading,
  onScan,
}: ScanListColumnProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="space-y-2 p-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (scans.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">{t('gymAdmin.scans.empty')}</p>;
  }

  return (
    <ul className="divide-y">
      {scans.map((scan) => {
        const archived = isScanArchived(scan);
        const selected = scan.id === selectedId;

        // Already being looked at: the only state there is nothing to ask for.
        // A `pending` scan is *waiting* to be looked at rather than queued —
        // since the upload stopped starting anything, pressing it is how a
        // setter jumps the hourly batch. A stalled one says `processing` but
        // nobody is looking at it any more, so it can be asked for again.
        const running = scan.status === 'processing' && !scan.stalled;
        // A wall that has never answered is being looked at, not looked at
        // *again*. **Which it is follows from the answer, not from the rows** —
        // a wall whose holds are all still proposals has been looked at, and
        // calling that press a first look was what hid the dialog on exactly
        // those walls. See hasScanAnswer.
        const label = hasScanAnswer(scan)
          ? t('gymAdmin.scans.rescan.scan')
          : t('climbScan.scanNow');

        return (
          // A row rather than one button, because the scan control is a button
          // of its own and a button cannot be nested inside another.
          <li key={scan.id} className={`flex items-center ${selected ? 'bg-muted' : ''}`}>
            <button
              type="button"
              onClick={() => onSelect(scan.id)}
              aria-current={selected}
              data-archived={archived}
              className={`flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/60 ${
                selected ? 'bg-muted' : ''
              } ${archived ? 'opacity-50 grayscale' : ''}`}
            >
              {scan.image_small_url || scan.image_url ? (
                <img
                  src={scan.image_small_url ?? scan.image_url ?? undefined}
                  alt=""
                  className="size-12 shrink-0 rounded object-cover"
                />
              ) : (
                <span className="size-12 shrink-0 rounded bg-muted" />
              )}

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{formatTaken(scan.created_at)}</span>
                <span className="mt-0.5 flex items-center gap-1.5">
                  <Avatar className="size-4">
                    <AvatarImage src={scan.user?.avatar_url ?? undefined} alt="" />
                    <AvatarFallback className="text-[8px]">
                      {(scan.user?.name ?? '?').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate text-xs text-muted-foreground">
                    {scan.user?.name ?? t('gymAdmin.scans.unknownUploader')}
                  </span>
                </span>
              </span>

              <span className="flex shrink-0 flex-col items-end gap-1">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONES[scan.status]}`}>
                  {t(`climbScan.status.${scan.status}`)}
                </span>
                <span className="text-[10px] uppercase text-muted-foreground">
                  {t(`gymAdmin.scans.climbType.${scan.climb_type}`)}
                </span>
              </span>
            </button>

            {onScan && (
              <button
                type="button"
                onClick={() => onScan(scan.id)}
                disabled={running}
                aria-label={label}
                title={label}
                data-testid="scan-row-rescan"
                className="mr-2 shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              >
                <RefreshCw className={`size-4 ${running ? 'animate-spin' : ''}`} aria-hidden />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
